import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AuthTabSwitcher, type AuthTab } from '@/components/auth/AuthTabSwitcher';
import { useLoginDrawer } from '@/contexts/LoginDrawerContext';
import { useRegisterOverlay } from '@/contexts/OverlayStackContext';
import { shouldDismissSheet } from '@/utils/sheetDismiss';

export function LoginDrawer() {
  const { isOpen, closeLoginDrawer } = useLoginDrawer();
  const [tab, setTab] = useState<AuthTab>('login');
  const panelRef = useRef<HTMLDivElement>(null);
  const drag = useRef({ tracking: false, startX: 0, startY: 0, dy: 0, dx: 0 });

  useEffect(() => {
    if (isOpen) {
      setTab('login');
    }
  }, [isOpen]);

  useRegisterOverlay(isOpen, closeLoginDrawer);

  const resetDrag = () => {
    drag.current = { tracking: false, startX: 0, startY: 0, dy: 0, dx: 0 };
    if (panelRef.current) {
      panelRef.current.style.transform = '';
      panelRef.current.style.transition = 'transform 0.2s ease-out';
    }
  };

  const onDragStart = (event: React.TouchEvent) => {
    const touch = event.touches[0];
    if (!touch) return;
    drag.current = {
      tracking: true,
      startX: touch.clientX,
      startY: touch.clientY,
      dy: 0,
      dx: 0,
    };
    if (panelRef.current) {
      panelRef.current.style.transition = 'none';
    }
  };

  const onDragMove = (event: React.TouchEvent) => {
    if (!drag.current.tracking) return;
    const touch = event.touches[0];
    if (!touch) return;
    const dy = touch.clientY - drag.current.startY;
    const dx = touch.clientX - drag.current.startX;
    drag.current.dy = dy;
    drag.current.dx = dx;
    if (dy <= 0 || Math.abs(dx) > dy) return;
    if (panelRef.current) {
      panelRef.current.style.transform = `translateY(${dy}px)`;
    }
  };

  const onDragEnd = () => {
    if (!drag.current.tracking) return;
    const { dy, dx } = drag.current;
    if (shouldDismissSheet({ deltaY: dy, deltaX: dx })) {
      resetDrag();
      closeLoginDrawer();
      return;
    }
    resetDrag();
  };

  const handleAuthenticated = () => {
    closeLoginDrawer();
  };

  if (!isOpen) {
    return null;
  }

  const sheet = (
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50"
      onClick={closeLoginDrawer}
      data-testid="login-drawer-backdrop"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Masuk atau daftar"
        data-testid="login-drawer"
        className="w-full max-w-md bg-white rounded-t-3xl px-5 pt-3 pb-safe-4 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(event) => event.stopPropagation()}
      >
        <div
          data-testid="login-drawer-handle"
          className="flex justify-center mb-4 py-2 -mt-1"
          onTouchStart={onDragStart}
          onTouchMove={onDragMove}
          onTouchEnd={onDragEnd}
          onTouchCancel={onDragEnd}
        >
          <span className="h-1 w-10 rounded-full bg-gray-300" />
        </div>

        <AuthTabSwitcher
          value={tab}
          onValueChange={setTab}
          stayOnCurrentPage
          onAuthenticated={handleAuthenticated}
          onForgotPassword={closeLoginDrawer}
        />
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}

export default LoginDrawer;
